import { useState } from "react";
import {
  DndContext, DragOverlay, PointerSensor, TouchSensor, KeyboardSensor,
  useSensor, useSensors, useDroppable, useDraggable, closestCorners,
} from "@dnd-kit/core";
import { Flag, GripVertical } from "lucide-react";
import { apiStaff } from "@/lib/api";
import { toast } from "sonner";
import { StatusPill, Card } from "@/components/admin";

/**
 * Reusable Kanban dispatch board with drag-and-drop reassignment.
 *
 * Props:
 *  - data: { technicians: [{id,name,on_duty}], board: { [techId|"unassigned"]: Job[] } }
 *  - onCardClick(jobId)
 *  - onReload()                — re-fetch the board after a successful reassign
 *  - role: "manager" | "dispatcher"  — manager shows final_price pill
 *  - testidPrefix: string      — e.g. "dispatch" or "d"  (cards get `${prefix}-card-${id}`)
 */
export default function DispatchBoardDnD({ data, onCardClick, onReload, role, testidPrefix = "dispatch" }) {
  const [active, setActive] = useState(null);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
    useSensor(TouchSensor, { activationConstraint: { delay: 180, tolerance: 6 } }),
    useSensor(KeyboardSensor),
  );

  const cols = [
    { id: "unassigned", name: "Unassigned" },
    ...data.technicians.map((t) => ({ id: t.id, name: t.name, on_duty: t.on_duty })),
  ];

  // Flat lookup so the DragOverlay can render the same card visual
  const allJobs = Object.values(data.board || {}).flat();
  const activeJob = active ? allJobs.find((j) => j.id === active.id) : null;

  function handleDragStart(e) {
    setActive({ id: e.active.id });
  }

  async function handleDragEnd(e) {
    const dragged = active;
    setActive(null);
    if (!e.over || !dragged) return;

    const toCol = e.over.id; // "unassigned" or a technician id
    // find source col from data
    const fromCol = Object.entries(data.board || {}).find(([, jobs]) =>
      (jobs || []).some((j) => j.id === dragged.id)
    )?.[0];
    if (!fromCol || fromCol === toCol) return;

    const job = (data.board[fromCol] || []).find((j) => j.id === dragged.id);
    if (!job) return;

    const newTechId = toCol === "unassigned" ? null : toCol;
    const techName = toCol === "unassigned"
      ? "Unassigned"
      : (cols.find((c) => c.id === toCol)?.name || "tech");

    // Optimistic toast
    const t = toast.loading(`Reassigning ${job.customer_name || job.appliance} → ${techName}…`);
    try {
      await apiStaff.patch(`/crm/jobs/${job.id}`, { technician_id: newTechId });
      toast.success(`Reassigned → ${techName}`, { id: t });
      onReload && onReload();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Reassign failed", { id: t });
    }
  }

  return (
    <Card title="Today's board" padded={false}>
      <DndContext
        sensors={sensors}
        collisionDetection={closestCorners}
        onDragStart={handleDragStart}
        onDragEnd={handleDragEnd}
        onDragCancel={() => setActive(null)}
      >
        <div className="overflow-x-auto p-3">
          <div className="grid gap-3 min-w-max" style={{ gridTemplateColumns: `repeat(${cols.length}, 280px)` }}>
            {cols.map((c) => (
              <DroppableColumn key={c.id} col={c} count={(data.board[c.id] || []).length}>
                {(data.board[c.id] || []).map((j) => (
                  <DraggableJobCard
                    key={j.id}
                    job={j}
                    role={role}
                    testid={`${testidPrefix}-card-${j.id}`}
                    onClick={() => onCardClick && onCardClick(j.id)}
                  />
                ))}
                {(data.board[c.id] || []).length === 0 && (
                  <div className="text-[11px] text-[#002060]/50 italic px-1 py-2 select-none">
                    Drop a job here
                  </div>
                )}
              </DroppableColumn>
            ))}
          </div>
        </div>
        <DragOverlay dropAnimation={{ duration: 180, easing: "cubic-bezier(0.18, 0.67, 0.6, 1.22)" }}>
          {activeJob ? (
            <div className="w-[260px] bg-[#FFFFFF] border-2 border-[#FF6600] rounded-md p-2.5 shadow-2xl rotate-[1.5deg]">
              <div className="font-medium text-[13px] capitalize truncate">{activeJob.appliance}</div>
              <div className="text-[11px] text-[#002060]/70 mt-0.5 truncate">{activeJob.customer_name}</div>
              <div className="text-[11px] text-[#002060]/50 mt-0.5 truncate">{activeJob.address}</div>
            </div>
          ) : null}
        </DragOverlay>
      </DndContext>
    </Card>
  );
}

// ============== Droppable column ==============
function DroppableColumn({ col, count, children }) {
  const { setNodeRef, isOver } = useDroppable({ id: col.id });
  return (
    <div
      ref={setNodeRef}
      data-testid={`dispatch-col-${col.id}`}
      className={`rounded-md p-3 transition-colors border ${
        isOver
          ? "bg-[#FF6600]/10 border-[#FF6600] border-dashed"
          : "bg-[#002060]/5 border-[#002060]/15"
      }`}
    >
      <div className="flex items-center justify-between mb-2.5">
        <div className="flex items-center gap-2 min-w-0">
          <div className="font-medium text-sm text-[#002060] truncate">{col.name}</div>
          <span className="text-[10px] text-[#002060]/50 num shrink-0">{count}</span>
        </div>
        {col.id !== "unassigned" && (
          <StatusPill tone={col.on_duty ? "emerald" : "slate"} label={col.on_duty ? "on duty" : "off"} />
        )}
      </div>
      <div className="space-y-2 min-h-[60px]">{children}</div>
    </div>
  );
}

// ============== Draggable job card ==============
function DraggableJobCard({ job, role, testid, onClick }) {
  const { attributes, listeners, setNodeRef, transform, isDragging } = useDraggable({
    id: job.id,
  });
  const style = transform
    ? { transform: `translate3d(${transform.x}px, ${transform.y}px, 0)` }
    : undefined;

  return (
    <div
      ref={setNodeRef}
      style={style}
      data-testid={testid}
      className={`group relative bg-[#FFFFFF] border rounded-md transition-all ${
        isDragging
          ? "opacity-30 border-[#FF6600]"
          : "border-[#002060]/15 hover:border-[#FF6600]/60 hover:shadow-sm"
      }`}
    >
      {/* Drag handle */}
      <button
        type="button"
        {...listeners}
        {...attributes}
        data-testid={`${testid}-handle`}
        className="absolute left-0 top-0 bottom-0 w-6 grid place-items-center text-[#002060]/30 hover:text-[#FF6600] hover:bg-[#FF6600]/10 rounded-l-md cursor-grab active:cursor-grabbing touch-none"
        aria-label={`Drag ${job.customer_name || job.appliance} to reassign`}
        title="Drag to reassign"
      >
        <GripVertical className="w-3.5 h-3.5" />
      </button>
      {/* Clickable card body — opens drawer */}
      <button
        type="button"
        onClick={onClick}
        className="w-full text-left pl-7 pr-2.5 py-2.5"
      >
        <div className="flex items-start justify-between gap-2">
          <div className="font-medium text-[13px] capitalize truncate">{job.appliance}</div>
          {job.manager_flag && <Flag className="w-3 h-3 text-[#FF6600] shrink-0" />}
        </div>
        <div className="text-[11px] text-[#002060]/70 mt-0.5 truncate">{job.customer_name}</div>
        <div className="text-[11px] text-[#002060]/50 mt-0.5 truncate">{job.address}</div>
        <div className="mt-2 flex items-center gap-2 flex-wrap">
          <StatusPill status={job.status} />
          {job.has_quote && <StatusPill tone="blue" label="quote ready" />}
          {role === "manager" && job.final_price != null && (
            <span className="text-[10px] text-[#002060] num font-semibold">
              ${Number(job.final_price).toFixed(0)}
            </span>
          )}
        </div>
      </button>
    </div>
  );
}
